import { pinAnchor, pinColourOverride, recolourPinSvg } from './pinArtwork'
import { sanitizePrintSvg, standSvgOnBottomEdge } from './printSvgSubset'

export interface PrintArtwork {
  /** The symbol's key on the wire: the type, plus the colour when it overrides the design. */
  key: string
  svg: string
  /** The feature's `height` in mm. */
  height: number
}

/**
 * A colour as the `#rrggbb` the print server's SVG subset accepts, or undefined.
 * A stored `#rrggbbaa` keeps its colour and drops the alpha, which the subset has
 * no room for inside a fill.
 */
export function printableHex(color: string | undefined): string | undefined {
  const m = /^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i.exec((color ?? '').trim())
  return m ? `#${m[1].toLowerCase()}` : undefined
}

/**
 * A pin's drawing as the server prints it, by the rules the map's sprites follow
 * (`pinArtwork`): repainted only when the stored colour overrides the type's
 * design, and standing on its tip or centred on its location as `pinAnchor`
 * says.
 *
 * `type` is the artwork actually drawn, which is not always the pin's own: a pin
 * printed with the generic drawing stands on that drawing's tip. `designRaw` is
 * that type's WITH-background drawing, against which an override is told apart
 * from the type's own colour written down.
 *
 * Throws on artwork the server could not draw, so the caller can fall back to
 * the generic pin through this same pipeline.
 */
export function preparePrintArtwork(
  type: string,
  raw: string,
  designRaw: string,
  withBackground: boolean,
  pinMm: number,
  color?: string,
): PrintArtwork {
  const override = printableHex(pinColourOverride(color, designRaw))
  const svg = sanitizePrintSvg(override ? recolourPinSvg(raw, override) : raw)
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
  const root = doc.documentElement
  if (root.localName !== 'svg' || doc.querySelector('parsererror'))
    throw new Error('Invalid pin artwork')
  const box = (root.getAttribute('viewBox') ?? '')
    .trim()
    .split(/[ ,]+/)
    .map(Number)
  if (box.length !== 4 || !box.every(Number.isFinite) || box[2] <= 0 || box[3] <= 0)
    throw new Error('Invalid pin viewBox')
  // Twice the size for a pin standing on its tip, because its viewBox is doubled
  // to put the tip at the server's centre anchor — the drawing itself still
  // prints at `pinMm`.
  const standsOnTip = pinAnchor(type, withBackground) === 'bottom'
  return {
    key: override ? `${type}--${override.slice(1)}` : type,
    svg: standsOnTip ? standSvgOnBottomEdge(svg) : svg,
    height: standsOnTip ? pinMm * 2 : pinMm,
  }
}
